<template>
  <div class="activity-detail-wrapper">
    <!-- 顶部栏 -->
    <div class="header">
      <div class="header-left">
        <a-button icon="arrow-left" size="small" @click="$router.back()" />
        <span class="page-type">详情</span>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="content">
      <!-- 基本信息条 + Tab 导航 -->
      <div class="common flex-row">
        <div class="left">
          <div class="basic-info">
            <div class="img-wrapper">
              <a-icon type="calendar" style="color: #237ffa; font-size: 18px;" />
            </div>
            <div>
              <div class="code">
                <span>活动编号：{{ detail.activityNo || '--' }}</span>
                <span :class="['status-' + detail.status]">{{ detail.statusDesc }}</span>
              </div>
              <div class="customer">{{ detail.name || '--' }}</div>
            </div>
          </div>
          <a-tabs v-model="activeTab" :animated="false">
            <a-tab-pane key="basic" tab="基本信息" />
            <a-tab-pane key="progress" tab="活动进展" />
            <a-tab-pane key="log" tab="日志动态" />
          </a-tabs>
        </div>
      </div>

      <!-- Tab: 基本信息 -->
      <template v-if="activeTab === 'basic'">
        <!-- 活动信息 -->
        <div class="common">
          <div class="title gap-12">活动信息</div>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">活动编号</span>
              <span class="info-value">{{ detail.activityNo || '--' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">活动标题</span>
              <span class="info-value">{{ detail.name || '--' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">有效期</span>
              <span class="info-value">
                <span v-if="detail.isPermanent">永久有效</span>
                <span v-else>{{ formatDate(detail.startDate) }} ~ {{ formatDate(detail.endDate) }}</span>
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">创建人</span>
              <span class="info-value">{{ detail.createUserName || '--' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ formatDateTime(detail.createTime) }}</span>
            </div>
            <div class="info-item info-item--full">
              <span class="info-label">活动描述</span>
              <span class="info-value">{{ detail.description || '--' }}</span>
            </div>
          </div>
        </div>

        <!-- 活动二维码 -->
        <div class="common">
          <div class="title gap-12">活动二维码</div>
          <div v-if="detail.qrCodeUrl" class="qrcode-box">
            <img :src="detail.qrCodeUrl" alt="活动二维码" class="qrcode-img" />
            <div class="qrcode-tip">扫码获取资料</div>
            <a-button type="link" size="small" class="action-link-btn" @click="downloadQrCode">
              <a-icon type="download" />下载二维码
            </a-button>
          </div>
          <a-empty v-else description="开启活动后生成二维码" style="padding: 12px 0" />
        </div>

        <!-- 活动资料 -->
        <div class="common">
          <div class="title gap-12">活动资料</div>
          <a-empty v-if="!detail.materials || detail.materials.length === 0" description="暂无资料" style="padding: 12px 0" />
          <div v-else class="material-list">
            <div
              v-for="(item, idx) in detail.materials"
              :key="item.id"
              class="material-item"
            >
              <span class="material-index">{{ idx + 1 }}</span>
              <a-icon type="file" class="material-icon" />
              <span class="material-name">{{ item.name }}</span>
              <span class="material-filename">（{{ item.fileName || '未知文件名' }}）</span>
              <a :href="getMaterialPreviewUrl(item)" target="_blank" class="material-link">预览</a>
            </div>
          </div>
        </div>
      </template>

      <!-- Tab: 活动进展 -->
      <template v-if="activeTab === 'progress'">
        <div class="common">
          <div class="title gap-12 title-with-action">
            <span>访客列表</span>
            <a-button type="link" size="small" class="action-link-btn" @click="handleExport">
              <a-icon type="export" />导出
            </a-button>
          </div>
          <a-table
            :columns="visitorColumns"
            :data-source="visitorList"
            :pagination="false"
            :loading="visitorLoading"
            row-key="id"
            size="small"
            :scroll="{ x: '100%' }"
          >
            <span slot="visitTime" slot-scope="text">{{ formatDateTime(text) }}</span>
            <span slot="downloadFileNames" slot-scope="text">{{ text || '--' }}</span>
          </a-table>
          <a-pagination
            v-if="visitorTotal > 0"
            :current="visitorPage.pageNum"
            :total="visitorTotal"
            :show-total="(t) => `共 ${t} 条`"
            show-size-changer
            :pageSizeOptions="['10', '20', '50']"
            show-quick-jumper
            :pageSize.sync="visitorPage.pageSize"
            style="margin-top: 12px; text-align: right"
            @change="handleVisitorPageChange"
            @showSizeChange="handleVisitorSizeChange"
          />
        </div>
      </template>

      <!-- Tab: 日志动态 -->
      <template v-if="activeTab === 'log'">
        <div class="common">
          <div class="title gap-12">操作日志</div>
          <a-timeline>
            <a-timeline-item
              v-for="item in logList"
              :key="item.id"
              :color="getLogColor(item.actionType)"
            >
              <div class="log-item">
                <span class="log-action">【{{ item.actionTypeDesc }}】</span>
                <span class="log-content">{{ item.actionContent }}</span>
                <div class="log-meta">
                  <span>{{ item.operatorName }}</span>
                  <span class="log-time">{{ formatDateTime(item.actionTime) }}</span>
                </div>
              </div>
            </a-timeline-item>
            <a-empty v-if="logList.length === 0" description="暂无日志" style="padding: 12px 0" />
          </a-timeline>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import {
  activityDetail,
  activityVisitorPageList,
  activityVisitorExportList,
  activityLogList,
} from '@/api/activity.js';

export default {
  name: 'ActivityDetail',
  data () {
    return {
      activeTab: 'basic',
      detail: {},
      logList: [],

      visitorList: [],
      visitorTotal: 0,
      visitorPage: { pageNum: 1, pageSize: 10 },
      visitorLoading: false,
      visitorColumns: [
        { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
        { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130 },
        { title: '组织/企业', dataIndex: 'orgName', key: 'orgName', width: 180, ellipsis: true },
        { title: '职位', dataIndex: 'position', key: 'position', width: 120 },
        { title: '访问时间', dataIndex: 'visitTime', key: 'visitTime', width: 160, scopedSlots: { customRender: 'visitTime' } },
        { title: '下载文件', dataIndex: 'downloadFileNames', key: 'downloadFileNames', ellipsis: true, scopedSlots: { customRender: 'downloadFileNames' } },
      ],
    };
  },

  computed: {
    activityId () {
      return this.$route.query.id;
    },
  },

  created () {
    this.loadDetail();
    this.loadLogs();
  },

  watch: {
    activeTab (val) {
      if (val === 'progress' && this.visitorList.length === 0) {
        this.loadVisitors();
      }
    },
  },

  methods: {
    async loadDetail () {
      const { body } = await activityDetail({ id: this.activityId });
      this.detail = body || {};
    },

    async loadLogs () {
      const { body } = await activityLogList({ id: this.activityId });
      this.logList = body || [];
    },

    async loadVisitors () {
      this.visitorLoading = true;
      try {
        const { body } = await activityVisitorPageList({
          activityId: this.activityId,
          ...this.visitorPage,
        });
        this.visitorList = body?.list || [];
        this.visitorTotal = (body?.total || 0) * 1;
      } finally {
        this.visitorLoading = false;
      }
    },

    handleVisitorPageChange (num) {
      this.visitorPage.pageNum = num;
      this.loadVisitors();
    },

    handleVisitorSizeChange (_, size) {
      this.visitorPage.pageSize = size;
      this.visitorPage.pageNum = 1;
      this.loadVisitors();
    },

    async handleExport () {
      const { body } = await activityVisitorExportList({ id: this.activityId });
      const list = body || [];
      if (list.length === 0) {
        this.$message.warning('暂无访客数据可导出');
        return;
      }
      const headers = ['姓名', '手机号', '组织/企业', '职位', '访问时间', '下载文件'];
      const rows = list.map((r) => [
        r.name, r.phone, r.orgName, r.position,
        r.visitTime ? dayjs(r.visitTime).format('YYYY-MM-DD HH:mm:ss') : '',
        r.downloadFileNames || '',
      ]);
      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `访客列表_${this.detail.activityNo}_${dayjs().format('YYYYMMDDHHmmss')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },

    async downloadQrCode () {
      try {
        const res = await fetch(this.detail.qrCodeUrl);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `二维码_${this.detail.activityNo}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        this.$message.error('二维码下载失败，请重试');
      }
    },

    getLogColor (actionType) {
      const map = { CREATE: 'green', OPEN: 'blue', CLOSE: 'gray', PLAN: 'orange' };
      return map[actionType] || 'blue';
    },

    getMaterialPreviewUrl (item) {
      if (!item.fileUrl) return '#';
      const fileName = item.fileName || '';
      // PDF / 图片可直接在浏览器内预览，保持原 URL
      if (/\.(pdf|png|jpg|jpeg|gif|webp|svg)$/i.test(fileName)) return item.fileUrl;
      // Word / Excel / PPT 等无法预览，追加 attname 让七牛在响应头带上正确文件名，
      // 确保浏览器下载后有正确的扩展名
      if (!fileName) return item.fileUrl;
      const sep = item.fileUrl.includes('?') ? '&' : '?';
      return `${item.fileUrl}${sep}attname=${encodeURIComponent(fileName)}`;
    },

    formatDate (date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : '--';
    },

    formatDateTime (date) {
      return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '--';
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin tag_round() {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: normal;
}

.activity-detail-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;

  .header {
    height: 52px;
    background-color: #fff;
    display: flex;
    align-items: center;
    padding: 0 12px;
    flex-shrink: 0;

    .header-left {
      display: flex;
      align-items: center;
    }

    .page-type {
      font-size: 16px;
      font-weight: bold;
      margin-left: 1em;
    }

  }

  .content {
    flex: 1;
    padding: 8px 0 0;
    overflow: auto;

    .gap-12 {
      margin-bottom: 12px;
    }

    .common {
      background-color: #fff;
      padding: 12px;
      margin-bottom: 6px;

      &.flex-row {
        display: flex;
        padding-right: 0;
        padding-bottom: 0;

        .left {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          .basic-info {
            display: flex;
            align-items: center;
            padding-bottom: 12px;

            .img-wrapper {
              width: 40px;
              height: 40px;
              background-color: #e2f3fe;
              border-radius: 4px;
              margin-right: 10px;
              display: flex;
              justify-content: center;
              align-items: center;
              flex-shrink: 0;
            }

            .code {
              font-size: 14px;
              color: #929aa6;
              margin-bottom: 4px;
              display: flex;
              align-items: center;
              gap: 8px;
            }

            .customer {
              font-size: 16px;
              font-weight: 600;
              color: #1f2733;
            }
          }

          /deep/ .ant-tabs-bar {
            margin: 0;
          }
        }
      }

      .title {
        font-weight: bold;
        font-size: 14px;
        color: #1f2733;
      }

      .title-with-action {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .action-link-btn {
        font-size: 13px;
        font-weight: normal;
        padding: 0 4px;
        height: auto;
        line-height: 1;
        color: #237ffa;

        .anticon {
          margin-right: 3px;
          font-size: 12px;
        }

        &:hover {
          color: #1a6fdc;
        }
      }

      .info-grid {
        display: flex;
        flex-wrap: wrap;
        margin-top: 4px;
      }

      .info-item {
        width: 33.333%;
        display: flex;
        align-items: flex-start;
        font-size: 14px;
        padding: 8px 12px 8px 0;
        line-height: 1.6;

        &--full {
          width: 100%;
        }
      }

      .info-label {
        flex-shrink: 0;
        width: 70px;
        color: #606266;
        margin-right: 4px;

        &::after {
          content: '：';
        }
      }

      .info-value {
        flex: 1;
        min-width: 0;
        color: #303133;
        word-break: break-word;
      }
    }
  }

  // 状态标签
  .status-0 {
    @include tag_round();
    background-color: #f2f4f7;
    color: #1f2733;
  }
  .status-1 {
    @include tag_round();
    background-color: #e2f3fe;
    color: #237ffa;
  }
  .status-2 {
    @include tag_round();
    background-color: #f2f4f7;
    color: #929aa6;
  }

  // 二维码
  .qrcode-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 4px 0;
  }
  .qrcode-img {
    width: 160px;
    height: 160px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
  }
  .qrcode-tip {
    font-size: 12px;
    color: #929aa6;
  }

  // 资料列表
  .material-list {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
  }
  .material-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    gap: 8px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 13px;

    &:last-child {
      border-bottom: none;
    }
  }
  .material-index {
    color: #bbb;
    width: 20px;
    flex-shrink: 0;
  }
  .material-icon {
    color: #1890ff;
    flex-shrink: 0;
  }
  .material-name {
    font-weight: 500;
    color: #1f2733;
  }
  .material-filename {
    color: #929aa6;
    font-size: 12px;
  }
  .material-link {
    margin-left: auto;
    color: #1890ff;
    font-size: 12px;
    flex-shrink: 0;
  }

  // 日志
  .log-item {
    padding: 2px 0;
  }
  .log-action {
    font-weight: 500;
    color: #1f2733;
  }
  .log-content {
    color: #555;
    margin-left: 4px;
  }
  .log-meta {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #929aa6;
    margin-top: 4px;
  }
  .log-time {
    color: #bbb;
  }
}
</style>
